import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome, faTags, faShoppingCart, faClock, faFileAlt, faHeart, faBell, faStar, faUserFriends,
  faCog, faUser, faLock, faLifeRing, faShieldAlt, faPlus, faChartBar, faEnvelopeOpenText, faBullhorn,
  faMoneyBillWave, faUsers, faBuilding, faCheckToSlot
} from '@fortawesome/free-solid-svg-icons';

const SidebarMenu = ({ selectedRole, onSelectPage }) => {
  const [openMenus, setOpenMenus] = useState({});
  const toggleSubMenu = (menu) => setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));

  const MenuItem = ({ icon, label, page }) => (
    <li
      onClick={() => onSelectPage(page, label)}
      className="cursor-pointer p-2 hover:bg-gray-600 text-white flex items-center gap-2"
    >
      <FontAwesomeIcon icon={icon} /> {label}
    </li>
  );

  const SubMenu = ({ label, icon, children }) => (
    <div>
      <div
        onClick={() => toggleSubMenu(label)}
        className="cursor-pointer p-2 hover:bg-gray-400 flex items-center justify-between"
      >
        <span className="flex items-center gap-2"><FontAwesomeIcon icon={icon} /> {label}</span>
        <span>{openMenus[label] ? '▲' : '▼'}</span>
      </div>
      {openMenus[label] && (
        <ul className="ml-4 border-l border-gray-300 pl-2">
          {children}
        </ul>
      )}
    </div>
  );

  // ----------------------------- ROLE MENUS -----------------------------

  const renderCustomerMenu = () => (
    <>
      <MenuItem icon={faTags} label="Browse Properties" page="browse-properties" />
      <SubMenu label="My Interests" icon={faShoppingCart}>
        <MenuItem icon={faShoppingCart} label="Shortlisted" page="shortlisted-properties" />
        <MenuItem icon={faClock} label="Visited" page="visited-properties" />
        <MenuItem icon={faFileAlt} label="Booked/Closed" page="booked-properties" />
      </SubMenu>
      <MenuItem icon={faHeart} label="Favorites" page="favorites" />
      <MenuItem icon={faBell} label="Notifications" page="notifications" />
      <MenuItem icon={faStar} label="My Reviews" page="my-reviews" />
      <MenuItem icon={faUserFriends} label="Refer & Earn" page="referral-program" />
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faUser} label="Profile" page="profile" />
        <MenuItem icon={faLock} label="Security" page="account-security" />
        <MenuItem icon={faCog} label="Preferences" page="preferences" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="support" />
    </>
  );

  const renderOwnerMenu = () => (
    <>
      <MenuItem icon={faShieldAlt} label="My Profile" page="owner-profile" />
      <SubMenu label="Property Listings" icon={faTags}>
        <MenuItem icon={faPlus} label="Add New Property" page="add-property" />
        <MenuItem icon={faTags} label="Manage Listings" page="manage-properties" />
        <MenuItem icon={faChartBar} label="Analytics" page="owner-analytics" />
      </SubMenu>
      <SubMenu label="Inquiries & Reviews" icon={faEnvelopeOpenText}>
        <MenuItem icon={faEnvelopeOpenText} label="Inquiries" page="property-inquiries" />
        <MenuItem icon={faStar} label="Reviews Received" page="owner-reviews" />
        <MenuItem icon={faBullhorn} label="Promote Listings" page="promotions" />
      </SubMenu>
      <SubMenu label="Earnings" icon={faMoneyBillWave}>
        <MenuItem icon={faMoneyBillWave} label="Payment History" page="owner-payments" />
        <MenuItem icon={faCog} label="Payout Settings" page="payout-settings" />
      </SubMenu>
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faBuilding} label="Business Info" page="owner-business-info" />
        <MenuItem icon={faLock} label="Security" page="owner-security" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="owner-support" />
    </>
  );

  const renderAgentMenu = () => (
    <>
      <MenuItem icon={faUser} label="My Agent Profile" page="agent-profile" />
      <SubMenu label="Manage Leads" icon={faEnvelopeOpenText}>
        <MenuItem icon={faEnvelopeOpenText} label="Incoming Leads" page="agent-leads" />
        <MenuItem icon={faBullhorn} label="Follow-ups" page="agent-followups" />
      </SubMenu>
      <SubMenu label="Property Coordination" icon={faTags}>
        <MenuItem icon={faTags} label="Assigned Properties" page="agent-properties" />
        <MenuItem icon={faClock} label="Site Visit Schedule" page="site-visits" />
      </SubMenu>
      <MenuItem icon={faChartBar} label="Performance Reports" page="agent-performance" />
      <MenuItem icon={faLifeRing} label="Support" page="agent-support" />
    </>
  );

  const renderAdminMenu = () => (
    <>
      <SubMenu label="User Management" icon={faUsers}>
        <MenuItem icon={faUsers} label="View All Users" page="admin-users" />
        <MenuItem icon={faUserFriends} label="Reported Issues" page="user-issues" />
      </SubMenu>
      <SubMenu label="Property Oversight" icon={faBuilding}>
        <MenuItem icon={faBuilding} label="Pending Listings" page="pending-properties" />
        <MenuItem icon={faBuilding} label="Approved Listings" page="approved-properties" />
        <MenuItem icon={faChartBar} label="Property Analytics" page="property-analytics" />
      </SubMenu>
      <SubMenu label="Moderation Center" icon={faTags}>
        <MenuItem icon={faCheckToSlot} label="Deal Approvals" page="deal-approvals" />
        <MenuItem icon={faFileAlt} label="Logs & History" page="moderation-logs" />
      </SubMenu>
      <SubMenu label="Marketing & Engagement" icon={faBullhorn}>
        <MenuItem icon={faEnvelopeOpenText} label="Email Campaigns" page="admin-emails" />
        <MenuItem icon={faBell} label="Push Notifications" page="admin-notifications" />
        <MenuItem icon={faTags} label="Featured Properties" page="featured-properties" />
      </SubMenu>
      <SubMenu label="Platform Analytics" icon={faChartBar}>
        <MenuItem icon={faChartBar} label="User Trends" page="user-trends" />
        <MenuItem icon={faMoneyBillWave} label="Revenue" page="admin-revenue" />
      </SubMenu>
      <SubMenu label="Admin Settings" icon={faCog}>
        <MenuItem icon={faCog} label="Config" page="admin-config" />
        <MenuItem icon={faLock} label="Security Logs" page="admin-security" />
        <MenuItem icon={faFileAlt} label="Compliance" page="compliance" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Admin Support" page="admin-support" />
    </>
  );

  // --------------------------- RENDER BY ROLE ----------------------------

  const renderMenuByRole = () => {
    switch (selectedRole) {
      case 'Customer': return renderCustomerMenu();
      case 'Owner': return renderOwnerMenu();
      case 'Agent': return renderAgentMenu();
      case 'Admin': return renderAdminMenu();
      default: return null;
    }
  };

  return (
    <div className="flex flex-col w-full sm:w-2/5 md:w-1/4 lg:w-1/5 bg-blue-500 text-white border-2 border-blue-300 shadow-md h-screen overflow-y-auto">
      <ul className="p-2">
        <MenuItem icon={faHome} label={`${selectedRole} Dashboard`} page="dashboard" />
        {renderMenuByRole()}
      </ul>
    </div>
  );
};

export default SidebarMenu;
